import { useSyncExternalStore } from "react";

/**
 * Routage minimal en chemins propres (pas de hash), pour des URLs partageables :
 *
 *   /                              la grille d'albums
 *   /album/:id                     un album
 *   /album/:id/photo/:photoId      une photo dans son album
 *   /a-trier                       les photos à trier
 *   /a-trier/photo/:photoId        une photo à trier, ouverte
 *
 * Un rechargement à froid fonctionne : le serveur renvoie index.html pour toute
 * route non-API (`setNotFoundHandler`), et Vite fait de même en dev.
 *
 * Pas de dépendance de routage : l'app a trois écrans, un abonnement à l'historique
 * suffit. L'état vit dans `window.history`, la source de vérité étant l'URL.
 */

export type Route =
  | { kind: "home" }
  | { kind: "album"; id: string; photoId: string | null }
  | { kind: "loose"; photoId: string | null };

export function parseRoute(pathname: string): Route {
  const seg = pathname.split("/").filter((s) => s.length > 0).map(decodeURIComponent);
  if (seg[0] === "album" && seg[1]) {
    return { kind: "album", id: seg[1], photoId: seg[2] === "photo" && seg[3] ? seg[3] : null };
  }
  if (seg[0] === "a-trier") {
    return { kind: "loose", photoId: seg[1] === "photo" && seg[2] ? seg[2] : null };
  }
  return { kind: "home" };
}

export function routePath(r: Route): string {
  if (r.kind === "album") {
    const base = `/album/${encodeURIComponent(r.id)}`;
    return r.photoId ? `${base}/photo/${encodeURIComponent(r.photoId)}` : base;
  }
  if (r.kind === "loose") {
    return r.photoId ? `/a-trier/photo/${encodeURIComponent(r.photoId)}` : "/a-trier";
  }
  return "/";
}

/**
 * L'« écran » d'une route, hors photo ouverte : ouvrir une photo est un calque
 * par-dessus la même page, ça ne change pas l'écran — donc ça ne doit ni remettre
 * le défilement en haut, ni déclencher de restauration.
 */
function screenKey(r: Route): string {
  return r.kind === "album" ? `album:${r.id}` : r.kind;
}

// --- Défilement mémorisé par entrée d'historique -----------------------------
// Revenir sur la grille ou sur « à trier » remonte le composant et relance sa
// requête : le navigateur ne peut pas restaurer un défilement dans un contenu qui
// n'existe pas encore. On mémorise donc la position par entrée, et on la réapplique
// image par image tant que le contenu grandit.

/**
 * `pushed` marque les entrées que **nous** avons empilées. Une entrée créée par le
 * navigateur (chargement à froid d'un lien partagé) reçoit une clé pour mémoriser son
 * défilement, mais pas `pushed` : sinon `goBack` ferait sortir du site.
 */
type HistoryState = { pushed?: true; key?: string };

if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";

const scrollByKey = new Map<string, number>();
let keySeq = 0;
const newKey = (): string => `k${++keySeq}`;

/** Conserve `pushed` de l'entrée courante : remplacer une URL ne change pas son origine. */
function stateWith(key: string, pushed?: true): HistoryState {
  const current = window.history.state as HistoryState | null;
  const garde = pushed ?? current?.pushed;
  return garde ? { key, pushed: garde } : { key };
}

/** Clé de l'entrée courante, créée à la volée si l'entrée vient du navigateur. */
function ensureKey(): string {
  const st = window.history.state as HistoryState | null;
  if (st?.key) return st.key;
  const k = newKey();
  window.history.replaceState(stateWith(k), "", window.location.pathname);
  return k;
}

let restoreRaf: number | null = null;

function cancelRestore(): void {
  if (restoreRaf !== null) {
    cancelAnimationFrame(restoreRaf);
    restoreRaf = null;
  }
}

/**
 * Réapplique la position jusqu'à ce qu'elle « tienne » (le contenu doit être assez
 * haut) ou jusqu'à expiration. Un geste de l'utilisateur annule : c'est lui qui
 * décide où il est.
 */
function restoreScroll(target: number): void {
  cancelRestore();
  if (target <= 0) {
    window.scrollTo(0, 0);
    return;
  }
  // 5 s et non 1,5 : le contenu arrive après un aller-retour réseau vers Cloud Run,
  // bien plus lent qu'en local — c'est ce qui faisait échouer la restauration à distance.
  const deadline = performance.now() + 5000;
  const step = (): void => {
    window.scrollTo(0, target);
    if (Math.abs(window.scrollY - target) < 2 || performance.now() > deadline) {
      restoreRaf = null;
      return;
    }
    restoreRaf = requestAnimationFrame(step);
  };
  restoreRaf = requestAnimationFrame(step);
}

/**
 * Annuler la restauration quand l'utilisateur **défile lui-même** — mais seulement
 * alors. Le premier jet annulait sur n'importe quel `wheel`, or le retour par geste à
 * deux doigts (trackpad macOS) émet précisément des `wheel` horizontaux : le geste
 * annulait donc la restauration qu'il venait de déclencher. Même piège au clavier, où
 * ⌘←/⌘[ sont des raccourcis de navigation et non de défilement.
 *
 * D'où les trois règles : molette **à dominante verticale** seulement, touches de
 * défilement **sans modificateur**, et `touchmove` plutôt que `touchstart` (un simple
 * contact, ou le début d'un balayage de bord, n'est pas une intention de défiler).
 */
const SCROLL_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
  "Spacebar",
]);

window.addEventListener(
  "wheel",
  (e) => {
    const dy = Math.abs(e.deltaY);
    if (dy > 2 && dy > Math.abs(e.deltaX)) cancelRestore();
  },
  { passive: true },
);
window.addEventListener(
  "keydown",
  (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return; // ⌘← = navigation, pas défilement
    if (SCROLL_KEYS.has(e.key)) cancelRestore();
  },
  { passive: true },
);
window.addEventListener("touchmove", cancelRestore, { passive: true });

// --- Store ------------------------------------------------------------------

const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/**
 * Une URL non reconnue affiche la home ; on réécrit alors l'adresse pour ne pas
 * laisser traîner un chemin inexistant dans la barre (lien partagé mal recopié).
 */
function normalize(): Route {
  const r = parseRoute(window.location.pathname);
  const canonical = routePath(r);
  if (canonical !== window.location.pathname) {
    const st = window.history.state as HistoryState | null;
    window.history.replaceState(stateWith(st?.key ?? newKey()), "", canonical);
  }
  return r;
}

let current: Route = normalize();
let currentKey: string = ensureKey();

export function navigate(r: Route, opts?: { replace?: boolean }): void {
  const path = routePath(r);
  const previous = current;
  scrollByKey.set(currentKey, window.scrollY);

  if (path !== window.location.pathname) {
    if (opts?.replace) {
      window.history.replaceState(stateWith(currentKey), "", path);
    } else {
      const key = newKey();
      window.history.pushState(stateWith(key, true), "", path);
      currentKey = key;
    }
  }
  current = r;
  emit();

  // Nouvel écran = on commence en haut. Ouvrir/fermer une photo n'est pas un
  // changement d'écran : le défilement de la page derrière est préservé.
  if (screenKey(previous) !== screenKey(r)) {
    cancelRestore();
    window.scrollTo(0, 0);
  }
}

/**
 * Fermer une vue : on revient en arrière si c'est l'app qui a empilé l'entrée,
 * sinon on remplace l'URL. Sans ça, fermer une photo ouverte depuis un lien partagé
 * ferait sortir du site.
 */
export function goBack(fallback: Route): void {
  // Uniquement si c'est nous qui avons empilé l'entrée : sinon on quitterait le site.
  if ((window.history.state as HistoryState | null)?.pushed) window.history.back();
  else navigate(fallback, { replace: true });
}

window.addEventListener("popstate", () => {
  // La position de l'entrée qu'on quitte, avant que le DOM ne change.
  scrollByKey.set(currentKey, window.scrollY);
  const previous = current;
  current = normalize();
  currentKey = ensureKey();
  emit();
  if (screenKey(previous) !== screenKey(current)) {
    restoreScroll(scrollByKey.get(currentKey) ?? 0);
  }
});

export function useRoute(): Route {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => current,
  );
}
